import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { checkException } from './useSidebarUtils';
import { Navigation, NavigationItemType } from './types';

const LOCAL_STORAGE_KEY = 'sidebarOpenMenuList';
const NOT_FOUND_INDEX = -1;

/**
 * Manages open/closed sidebar menu state and persists it in localStorage.
 */
export const useSidebarStorage = ({
  navigationConfig,
  exceptions,
}: {
  navigationConfig: Navigation;
  exceptions?: string[];
}) => {
  const { pathname } = useLocation();

  /** Reads the open menu list from localStorage. */
  const getOpenMenuList = (): string[] => {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) ?? '[]');
    } catch {
      return [];
    }
  };

  const [openMenuList, setOpenMenuList] = useState<string[]>(getOpenMenuList());

  /**
   * Toggles a menu section open or closed and saves it to localStorage.
   * @param itemName Menu name
   * @param isCollapsed If true, menu should remain collapsed
   */
  const updateOpenMenuList = (itemName: string, isCollapsed: boolean) => {
    const updated = [...openMenuList];
    const index = updated.indexOf(itemName);

    if (index === NOT_FOUND_INDEX && isCollapsed) return;

    if (index === NOT_FOUND_INDEX) {
      updated.push(itemName);
    } else {
      updated.splice(index, 1);
    }

    setOpenMenuList(updated);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
  };

  /**
   * Adds a menu to the open list if it's not already there.
   */
  const addToOpenMenuList = (itemName: string) => {
    const updated = openMenuList.includes(itemName) ? openMenuList : [...openMenuList, itemName];
    setOpenMenuList(updated);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
  };
  const getIsActive = (
    url: string,
    pathName: string,
    findMatchItem?: (items: Array<NavigationItemType>, pathName: string) => NavigationItemType,
    defaultValue = true,
  ): boolean => {
    let matchItem: any;

    if (findMatchItem) {
      matchItem = findMatchItem(navigationConfig.items, pathName);
    } else {
      navigationConfig.items.forEach((item: NavigationItemType) => {
        if (item.url === url) {
          matchItem = item;
        } else if (item.children) {
          const findMatch = item.children.find(child => child.url === url);
          if (findMatch) {
            matchItem = findMatch;
          }
        }
      });
    }

    let isActive;
    if (matchItem) {
      const exceptionUrls = [...(matchItem.exceptionUrls || [])];

      if (exceptions?.length) {
        exceptions.forEach(e => {
          if (!checkException(matchItem, e)) {
            exceptionUrls.push(e);
          }
        });
      }

      isActive = exceptionUrls.every(
        (exceptionUrl: string) => !pathName.startsWith(exceptionUrl) && !checkException(matchItem, exceptionUrl),
      );
    }
    return isActive === undefined ? defaultValue : isActive;
  };
  const getIsActiveRoute = (url: string) => {
    const isActiveRoute = getIsActive(url, pathname);
    return !isActiveRoute ? isActiveRoute : pathname.indexOf(url) !== NOT_FOUND_INDEX;
  };
  const getHasActiveSubItem = (subItems?: Array<NavigationItemType>): boolean => {
    if (!subItems) {
      return false;
    }

    return subItems.some(({ url, children }) => getIsActiveRoute(url) || getHasActiveSubItem(children));
  };
  const getNavWithOpenedActiveMenus = (items: Array<NavigationItemType>): Array<NavigationItemType> =>
    items.map((item: NavigationItemType) => {
      const index = openMenuList.indexOf(item.name);
      const isItemInOpenMenuList = index !== NOT_FOUND_INDEX;
      const hasActiveSubItem = getHasActiveSubItem(item.children);
      const shouldItemBeOpened = isItemInOpenMenuList || hasActiveSubItem;

      return {
        ...item,
        ...(shouldItemBeOpened && { isActive: true }),
        children: item.children && getNavWithOpenedActiveMenus(item.children),
      };
    });

  return {
    openMenuList,
    updateOpenMenuList,
    addToOpenMenuList,
    getNavWithOpenedActiveMenus,
  };
};
